import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Eye, X } from 'lucide-react';
import api from '../lib/axios';
import { HIDEABLE_NAV } from './Layout';

// Modules the super-admin may toggle per tenant, plus the "api" feature (off by default).
const FEATURES: { key: string; labelKey: string }[] = [
  ...HIDEABLE_NAV,
  { key: 'api', labelKey: 'settings.apiKeys' },
];

export default function TenantNavPanel({ slug, onClose }: Readonly<{ slug: string; onClose: () => void }>) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [saved, setSaved] = useState(false);

  const nav = useQuery<{ hidden: string[] }>({
    queryKey: ['tenant-navigation', slug],
    queryFn: () => api.get(`/tenants/${slug}/navigation`).then((r) => r.data),
  });

  useEffect(() => {
    if (nav.data) setHidden(new Set(nav.data.hidden));
  }, [nav.data]);

  const save = useMutation({
    mutationFn: () =>
      api.put(`/tenants/${slug}/navigation`, { hidden: Array.from(hidden) }).then((r) => r.data),
    onSuccess: () => {
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
      qc.invalidateQueries({ queryKey: ['tenant-navigation', slug] });
      qc.invalidateQueries({ queryKey: ['nav-visibility'] });
    },
  });

  const toggle = (key: string, visible: boolean) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (visible) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="reveal card mt-8 w-full max-w-lg p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <p className="eyebrow flex items-center gap-2">
              <Eye size={14} /> {t('settings.navVisibility')}
            </p>
            <h2 className="page-title mt-1 text-xl">{slug}</h2>
          </div>
          <button onClick={onClose} className="btn btn-secondary">
            <X size={16} /> {t('common.close')}
          </button>
        </div>

        <p className="mb-4 text-sm text-ink-muted">{t('settings.navVisibilityHint')}</p>

        <div className="space-y-2">
          {FEATURES.map((item) => {
            const visible = !hidden.has(item.key);
            return (
              <label
                key={item.key}
                className="flex items-center justify-between gap-3 rounded-lg border border-line px-3 py-2 text-sm"
              >
                <span className={visible ? 'font-medium text-ink' : 'text-ink-muted'}>
                  {t(item.labelKey)}
                </span>
                <input
                  type="checkbox"
                  checked={visible}
                  onChange={(e) => toggle(item.key, e.target.checked)}
                  aria-label={t(item.labelKey)}
                />
              </label>
            );
          })}
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button onClick={() => save.mutate()} disabled={save.isPending} className="btn btn-primary">
            {t('common.save')}
          </button>
          {saved && <span className="text-sm font-medium text-income">{t('settings.saved')}</span>}
        </div>
      </div>
    </div>
  );
}
